import {
  Close as CloseIcon,
  Settings as SettingsIcon,
  Email as EmailIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  ContentCopy as ContentCopyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Paper,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Avatar,
  Tooltip,
  Zoom,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Step, WorkFlow } from "0.Mine/3.Interfaces/workFlow";

// Styled components
const StyledTab = styled(Tab)(({ theme }) => ({
  minWidth: 100,
  fontWeight: 500,
  borderRadius: "8px 8px 0 0",
  transition: "all 0.3s",
  "&.Mui-selected": {
    backgroundColor: theme.palette.primary.light,
    color: "#ffffff!important",
  },
}));

const AnimatedButton = styled(Button)(({ theme }) => ({
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
  },
}));

// TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`workflow-tabpanel-${index}`}
      aria-labelledby={`workflow-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Main Workflow Modal Component
interface WorkflowModalProps {
  open: boolean;
  onClose: () => void;
  workflowData?: WorkFlow;
  onSave?: (workflow: WorkFlow) => Promise<void>;
}

export const WorkflowModal: React.FC<WorkflowModalProps> = ({
  open,
  onClose,
  workflowData,
  onSave,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  // Workflow state
  const [workflow, setWorkflow] = useState<WorkFlow>(workflowData);
  const [selectedStep, setSelectedStep] = useState<number | null>(null);

  // Initialize workflow data when modal opens
  useEffect(() => {
    if (workflowData) {
      const mappedSteps = workflowData.steps.map((step: any) => ({
        ...step,
        delayUnit: step.delay_unit,
      }));

      setWorkflow({
        ...workflowData,
        steps: mappedSteps,
      });
    }
  }, [workflowData]);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Handle workflow setting changes
  const handleSettingChange = (setting: keyof WorkFlow, value: boolean) => {
    if (!workflow) return;

    setWorkflow({
      ...workflow,
    });
  };

  // Handle general workflow property changes
  const handleWorkflowChange = (prop: keyof WorkFlow, value: any) => {
    if (!workflow) return;

    setWorkflow({
      ...workflow,
      [prop]: value,
    });
  };

  // Handle step changes
  const handleStepChange = (stepIndex: number, prop: keyof Step, value: any) => {
    if (!workflow) return;

    const updatedSteps = [...workflow.steps];
    updatedSteps[stepIndex] = {
      ...updatedSteps[stepIndex],
      [prop]: value,
    };

    setWorkflow({
      ...workflow,
      steps: updatedSteps,
    });
  };

  // Handle moving steps up and down
  const moveStep = (stepIndex: number, direction: "up" | "down") => {
    if (!workflow) return;

    const newPosition = direction === "up" ? stepIndex - 1 : stepIndex + 1;
    if (newPosition < 0 || newPosition >= workflow.steps.length) return;

    const updatedSteps = [...workflow.steps];
    const stepToMove = { ...updatedSteps[stepIndex] };
    const stepToReplace = { ...updatedSteps[newPosition] };

    // Swap positions
    stepToMove.position = newPosition + 1;
    stepToReplace.position = stepIndex + 1;

    updatedSteps[stepIndex] = stepToReplace;
    updatedSteps[newPosition] = stepToMove;

    // Sort by position
    updatedSteps.sort((a, b) => a.position - b.position);

    setWorkflow({
      ...workflow,
      steps: updatedSteps,
    });
  };

  // Handle adding a new step
  const addStep = () => {
    if (!workflow) return;

    const newPosition = workflow.steps.length + 1;
    const newId = Math.max(...workflow.steps.map((s) => s.id), 0) + 1;

    // Create new empty step with default values
    const newStep: Step = {
      id: newId,
      position: newPosition,
      title: `Nuovo Step ${newPosition}`,
      description: "Aggiungi una descrizione",
      type: workflow.templateType || "email",
      content: "",
      subject: "",
      delay: 1,
      delayUnit: "giorni",
    };

    // Add the new step to the workflow
    const updatedWorkflow = {
      ...workflow,
      steps: [...workflow.steps, newStep],
    };

    // Update the workflow state
    setWorkflow(updatedWorkflow);

    // Select the new step
    setSelectedStep(updatedWorkflow.steps.length - 1);
    setActiveTab(1); // Switch to steps tab
  };

  // Handle removing a step
  const removeStep = (stepIndex: number) => {
    if (!workflow) return;

    const updatedSteps = workflow.steps.filter((_, index) => index !== stepIndex);

    // Update positions
    updatedSteps.forEach((step, index) => {
      step.position = index + 1;
    });

    setWorkflow({
      ...workflow,
      steps: updatedSteps,
    });

    // Reset selected step if needed
    if (selectedStep === stepIndex || updatedSteps.length === 0) {
      setSelectedStep(null);
    } else if (selectedStep && selectedStep > stepIndex) {
      setSelectedStep(selectedStep - 1);
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!workflow || !onSave) return;

    try {
      setSaving(true);
      await onSave(workflow);
      setSuccess(true);

      // Reset success state after animation
      setTimeout(() => {
        setSuccess(false);
        setEditMode(false);
      }, 1500);
    } catch (error) {
      console.error("Error saving workflow:", error);
    } finally {
      setSaving(false);
    }
  };
  console.log("Rendering step:", workflow);
  // If no workflow data yet, show loading
  if (!workflow) {
    return (
      <Dialog
        open={open}
        fullScreen={fullScreen}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 500 }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 500 }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box display="flex" alignItems="center">
          <Avatar
            sx={{
              bgcolor: theme.palette.primary.main,
              mr: 2,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            <EmailIcon />
          </Avatar>
          <Typography variant="h6" component="div" sx={{ fontWeight: 500 }}>
            {workflow.templateName}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {editMode ? (
            <>
              {saving ? (
                <CircularProgress size={24} />
              ) : success ? (
                <CheckCircleIcon color="success" />
              ) : (
                <AnimatedButton
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  size="small"
                >
                  Salva
                </AnimatedButton>
              )}
              <AnimatedButton color="inherit" onClick={() => setEditMode(false)} size="small">
                Annulla
              </AnimatedButton>
            </>
          ) : (
            <AnimatedButton
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => setEditMode(true)}
              size="small"
            >
              Modifica
            </AnimatedButton>
          )}
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="workflow tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 2 }}
        >
          <StyledTab label="Panoramica" icon={<SettingsIcon />} iconPosition="start" />
          <StyledTab
            label={`Steps (${workflow.steps.length})`}
            icon={<EmailIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <DialogContent dividers>
        <TabPanel value={activeTab} index={0}>
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 3, height: "100%", borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Workflow Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label={t("commonText.name")}
                        fullWidth
                        value={workflow.templateName}
                        onChange={(e) => handleWorkflowChange("templateName", e.target.value)}
                        disabled={!editMode}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={t("commonText.type")}
                        fullWidth
                        value={workflow.templateType}
                        disabled
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper elevation={3} sx={{ p: 3, borderRadius: 2, boxShadow: "unset!important" }}>
                  <Typography variant="h6" gutterBottom>
                    Timeline del Workflow
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                    {workflow.steps.map((step, index) => (
                      <Box
                        key={step.id}
                        component={motion.div}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            mr: 2,
                          }}
                        >
                          {step.position}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight={500} sx={{ fontSize: "18px" }}>
                            {step.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "14px" }}
                          >
                            {step.description}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Chip
                            icon={<ScheduleIcon fontSize="small" />}
                            label={
                              step.delay === 0
                                ? t("automationPopup.now")
                                : `${step.delay} ${t(`automationPopup.${step.delayUnit}`)}`
                            }
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          {editMode && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedStep(index);
                                setActiveTab(1);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={3}
                  sx={{ height: "100%", boxShadow: "unset!important", padding: "0px" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    {editMode && (
                      <Tooltip title="Add Step">
                        <IconButton color="primary" onClick={addStep}>
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ maxHeight: 500, overflow: "auto" }}>
                    {workflow.steps.map((step, index) => (
                      <motion.div
                        key={step.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                      >
                        <Paper
                          elevation={selectedStep === index ? 3 : 1}
                          sx={{
                            padding: "15px 8px",
                            mb: 1,
                            cursor: "pointer",
                            backgroundColor: selectedStep === index ? "#E2E2EE" : "inherit",
                            transition: "all 0.2s ease",
                            boxShadow: "unset!important",
                            border: "1px solid #e0e0e0",
                            borderLeft:
                              selectedStep === index
                                ? `4px solid ${theme.palette.primary.main}`
                                : "1px solid #e0e0e0",
                            borderRadius: "10px",
                            "&:hover": {
                              backgroundColor: "#E2E2EE",
                            },
                          }}
                          onClick={() => setSelectedStep(index)}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              boxShadow: "unset!important",
                              borderRadius: "0",
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body1"
                                noWrap
                                sx={{ fontWeight: 500, maxWidth: "220px", fontSize: "15px" }}
                              >
                                {step.title}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                                sx={{ maxWidth: "220px", fontSize: "14px" }}
                              >
                                {step.description}
                              </Typography>
                            </Box>
                            {editMode && (
                              <Box>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeStep(index);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        </Paper>
                      </motion.div>
                    ))}
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={8}>
                <AnimatePresence mode="wait">
                  {selectedStep !== null ? (
                    <motion.div
                      key={`step-${selectedStep}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Paper
                        elevation={3}
                        sx={{
                          p: 3,
                          height: "100%",
                          borderRadius: "18px",
                          border: "1px solid #ebe8e8",
                          boxShadow: "unset!important",
                          marginTop: "7px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 3,
                          }}
                        >
                          {editMode && (
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Tooltip title="Move Up">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={selectedStep === 0}
                                    onClick={() => moveStep(selectedStep, "up")}
                                  >
                                    <ArrowUpwardIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Move Down">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={selectedStep === workflow.steps.length - 1}
                                    onClick={() => moveStep(selectedStep, "down")}
                                  >
                                    <ArrowDownwardIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Duplicate">
                                <IconButton size="small">
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              label={t("commonText.title")}
                              fullWidth
                              value={workflow.steps[selectedStep].title}
                              onChange={(e) =>
                                handleStepChange(selectedStep, "title", e.target.value)
                              }
                              disabled={!editMode}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label={t("commonText.description")}
                              fullWidth
                              value={workflow.steps[selectedStep].description}
                              onChange={(e) =>
                                handleStepChange(selectedStep, "description", e.target.value)
                              }
                              disabled={!editMode}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label={t("commonText.subject")}
                              fullWidth
                              value={workflow.steps[selectedStep].subject}
                              onChange={(e) =>
                                handleStepChange(selectedStep, "subject", e.target.value)
                              }
                              disabled={!editMode}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label={t("commonText.delay")}
                              type="number"
                              fullWidth
                              value={workflow.steps[selectedStep].delay}
                              onChange={(e) =>
                                handleStepChange(selectedStep, "delay", parseInt(e.target.value))
                              }
                              disabled={!editMode}
                              variant="outlined"
                              InputProps={{ inputProps: { min: 0 } }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth variant="outlined" disabled={!editMode}>
                              <InputLabel>{t("commonText.delayUnits")}</InputLabel>
                              <Select
                                defaultValue="days"
                                value={workflow.steps[selectedStep].delayUnit}
                                onChange={(e) =>
                                  handleStepChange(selectedStep, "delayUnit", e.target.value)
                                }
                                label={t("commonText.delayUnits")}
                              >
                                <MenuItem value="minutes">{t("automationPopup.minutes")}</MenuItem>
                                <MenuItem value="hours">{t("automationPopup.hours")}</MenuItem>
                                <MenuItem value="days">{t("automationPopup.days")}</MenuItem>
                                <MenuItem value="weeks">{t("automationPopup.weeks")}</MenuItem>
                                <MenuItem value="months">{t("automationPopup.months")}</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label={t("commonText.emailContent")}
                              fullWidth
                              multiline
                              rows={8}
                              value={workflow.steps[selectedStep].content}
                              onChange={(e) =>
                                handleStepChange(selectedStep, "content", e.target.value)
                              }
                              disabled={!editMode}
                              variant="outlined"
                              helperText={t("automationPopup.helperText")}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Paper
                        elevation={3}
                        sx={{
                          p: 4,
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: "400px",
                          borderRadius: "18px",
                          border: "1px solid #ebe8e8",
                          boxShadow: "unset!important",
                          marginTop: "7px",
                        }}
                      >
                        <EmailIcon
                          sx={{ fontSize: 60, color: "text.secondary", mb: 2, opacity: 0.3 }}
                        />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          Seleziona un passaggio per visualizzare i dettagli
                        </Typography>
                        {editMode && (
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={addStep}
                            sx={{ mt: 2 }}
                          >
                            {t("workflows.addStep")}
                          </Button>
                        )}
                      </Paper>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Box>
          <Button onClick={onClose} color="primary">
            Chiudi
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
